/**
 * Utilidades optimizadas para comparación de arrays
 * Evita el costoso JSON.stringify en arrays grandes
 */

/**
 * Compara dos arrays de manera eficiente usando shallow comparison
 * Optimizado para arrays de objetos con propiedades identificadoras
 *
 * @param arr1 - Primer array a comparar
 * @param arr2 - Segundo array a comparar
 * @param idKey - Clave única para identificar elementos (ej: 'id', 'enrollment_number')
 * @returns true si los arrays son equivalentes, false en caso contrario
 */
export const shallowCompareArrays = <T extends Record<string, any>>(
  arr1: T[],
  arr2: T[],
  idKey: keyof T,
): boolean => {
  // Comparación rápida de longitudes
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Arrays vacíos son iguales
  if (arr1.length === 0) {
    return true;
  }

  // Crear Set de IDs para comparación O(n) en lugar de O(n²)
  const ids1 = new Set(arr1.map((item) => item[idKey]));
  const ids2 = new Set(arr2.map((item) => item[idKey]));

  // Comparar tamaños de sets
  if (ids1.size !== ids2.size) {
    return false;
  }

  // Verificar que todos los IDs del primer array estén en el segundo
  for (const id of ids1) {
    if (!ids2.has(id)) {
      return false;
    }
  }

  return true;
};

/**
 * Genera un hash simple y rápido para arrays de objetos
 * Útil cuando solo necesitas detectar cambios sin comparar contenido específico
 *
 * @param arr - Array de objetos
 * @param idKey - Clave única para identificar elementos
 * @returns hash string del array
 */
export const generateArrayHash = <T extends Record<string, any>>(
  arr: T[],
  idKey: keyof T,
): string => {
  if (arr.length === 0) return 'empty';

  // Crear hash basado en longitud + primer y último ID + suma de algunos IDs
  const firstId = arr[0]?.[idKey] ?? '';
  const lastId = arr[arr.length - 1]?.[idKey] ?? '';
  const middleId = arr[Math.floor(arr.length / 2)]?.[idKey] ?? '';

  return `${arr.length}-${firstId}-${middleId}-${lastId}`;
};

/**
 * Comparación híbrida: usa hash para detección rápida y shallow comparison para verificación
 * Máxima performance para casos comunes
 *
 * @param arr1 - Primer array
 * @param arr2 - Segundo array
 * @param idKey - Clave única para identificar elementos
 * @returns true si los arrays son equivalentes
 */
export const optimizedArrayComparison = <T extends Record<string, any>>(
  arr1: T[],
  arr2: T[],
  idKey: keyof T,
): boolean => {
  // Paso 1: Comparación rápida con hash (O(1) en caso de diferencia)
  const hash1 = generateArrayHash(arr1, idKey);
  const hash2 = generateArrayHash(arr2, idKey);

  if (hash1 !== hash2) {
    return false;
  }

  // Paso 2: Si los hashes coinciden, verificar con shallow comparison
  return shallowCompareArrays(arr1, arr2, idKey);
};
